// La nota de venta en papel o PDF (docs/108 B8): para el cliente o para la bodega. No es un documento
// tributario (eso es la factura o boleta, que sale del DTE con su timbre). Formato a la Random
// (pedido de Rodrigo 2026-10-21): empresa con logo a la izquierda, recuadro con el número a la
// derecha, recuadro del cliente y el detalle sin numerar. Mientras la nota no está guardada y
// confirmada se imprime como DOCUMENTO EN CONSTRUCCIÓN (igual que el borrador de Random).
import type { CargoNota, Empresa, LineaNota, Nota, Totales } from '../api/contratos';
import logo from '../assets/logo-madeci.png';
import { decimal, fecha, pesos, rut } from '../comun/formato';

export interface DatosNotaImpresa {
  numeroVisible: string | null;
  cabecera: Nota['cabecera'];
  lineas: LineaNota[];
  cargos: CargoNota[];
  totales: Totales;
}

const CONDICION: Record<string, string> = {
  contado: 'Contado',
  credito: 'Crédito',
  cheque: 'Cheque',
  transferencia: 'Transferencia',
};

export function NotaImpresa({
  n,
  empresa,
  vendedor,
  comuna,
  enConstruccion = false,
}: {
  n: DatosNotaImpresa;
  empresa: Empresa | null;
  vendedor?: string | null;
  comuna?: string | null;
  enConstruccion?: boolean;
}) {
  const k = n.cabecera;
  const unidades = n.lineas.reduce((s, l) => s + Number(l.cantidad), 0);
  return (
    <article className={`impreso impreso-nv ${enConstruccion ? 'en-construccion' : ''}`}>
      {enConstruccion && (
        <div className="marca-agua" aria-hidden>
          DOCUMENTO EN CONSTRUCCIÓN
        </div>
      )}
      <header className="ir-cabecera">
        <div className="ir-emisor">
          <img src={logo} alt="MADECI" className="ir-logo" />
          <div>
            <b>{empresa?.razon_social ?? ''}</b>
            {empresa?.giro && <span>{empresa.giro}</span>}
            {empresa?.direccion && <span>{empresa.direccion}</span>}
            {empresa?.comuna && <span>{empresa.comuna}</span>}
            {empresa?.correo_contacto && <span>E-mail: {empresa.correo_contacto}</span>}
          </div>
        </div>
        <div className="ir-recuadro">
          <b>R.U.T. : {empresa ? rut(empresa.rut, empresa.dv) : ''}</b>
          <b className="ir-tipo">NOTA DE VENTA</b>
          <b>Nro.: {n.numeroVisible ?? 'SIN NÚMERO'}</b>
        </div>
      </header>
      {enConstruccion && (
        <p className="ir-aviso">
          DOCUMENTO EN CONSTRUCCIÓN · sin validez: la nota todavía no está confirmada
        </p>
      )}
      <section className="ir-receptor">
        <dl>
          <dt>Señor(es)</dt>
          <dd>: {k.clienteRazonSocial}</dd>
          <dt>R.U.T.</dt>
          <dd>: {k.clienteRut ? rut(k.clienteRut) : '—'}</dd>
          <dt>Giro</dt>
          <dd>: {k.clienteGiro ?? ''}</dd>
          <dt>Dirección</dt>
          <dd>: {k.direccionEntrega ?? ''}</dd>
          <dt>Comuna</dt>
          <dd>: {comuna ?? k.zonaLogisticaNombre ?? ''}</dd>
        </dl>
        <dl>
          <dt>F. de Emisión</dt>
          <dd>: {fecha(k.fechaNegocio)}</dd>
          <dt>F. Vencimi.</dt>
          <dd>: {k.fechaVencimiento ? fecha(k.fechaVencimiento) : fecha(k.fechaNegocio)}</dd>
          <dt>Condición</dt>
          <dd>
            : {CONDICION[k.condicionPago.toLowerCase()] ?? k.condicionPago}
            {k.plazoDias ? ` ${k.plazoDias} días` : ''}
          </dd>
          <dt>Documento</dt>
          <dd>: {k.documentoSolicitado === 'BOLETA' ? 'Boleta' : 'Factura'}</dd>
          <dt>Vendedor</dt>
          <dd>: {vendedor ?? ''}</dd>
        </dl>
      </section>
      <table className="ir-detalle" aria-label="Detalle">
        <thead>
          <tr>
            <th className="der">Cant.</th>
            <th>Código</th>
            <th>Detalle</th>
            <th className="der">P.Unitario</th>
            <th className="der">Descto.</th>
            <th className="der">Totales</th>
          </tr>
        </thead>
        <tbody>
          {n.lineas.map((l) => (
            <tr key={l.numero}>
              <td className="der">
                {decimal(l.cantidad)} {l.presentacionCodigo !== 'UN' ? l.presentacionCodigo : ''}
              </td>
              <td className="mono">{l.sku}</td>
              <td>{l.descripcion}</td>
              <td className="der">{pesos(l.precioAplicado)}</td>
              <td className="der">{Number(l.descuento) ? pesos(l.descuento) : '0'}</td>
              <td className="der">{pesos(l.bruto)}</td>
            </tr>
          ))}
          {n.cargos.map((c) => (
            <tr key={c.tipo}>
              <td />
              <td />
              <td>{c.descripcion}</td>
              <td />
              <td />
              <td className="der">{pesos(c.bruto)}</td>
            </tr>
          ))}
          <tr className="ir-relleno">
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
          </tr>
        </tbody>
      </table>
      <section className="ir-pie">
        <div className="ir-observaciones">
          <b>Observaciones:</b> {k.observaciones ?? ''}
          <span className="ir-unidades">Total Unidades: {decimal(String(unidades))}</span>
        </div>
        <dl className="ir-totales">
          <dt>Neto</dt>
          <dd>{pesos(n.totales.neto)}</dd>
          {n.totales.impuestos.map((i) => (
            <div key={i.codigo} className="contents">
              <dt>{i.codigo === 'IVA' ? 'I.V.A. (19%)' : i.codigo}</dt>
              <dd>{pesos(i.monto)}</dd>
            </div>
          ))}
          <dt className="total">Total</dt>
          <dd className="total">{pesos(n.totales.bruto)}</dd>
        </dl>
      </section>
      <p className="ir-legal">Documento interno: no reemplaza la factura ni la boleta.</p>
    </article>
  );
}
